import { AmenityGroup, InstahomeAmenityType } from "@instacover/helpers"
import cuid2 from "@paralleldrive/cuid2"
import Dexie from "dexie"

import { GeoJsonObj } from "@/components/Map"
import { Amenity, Photo, TableNames } from "@/database/helpers/tables"
import { DexieExtended } from "@/database/helpers/types"
import { AmenityRepository } from "@/database/repositories/amenity"
import { DetailRepository } from "@/database/repositories/detail"
import { OrderedStepRepository } from "@/database/repositories/orderedStep"
import { PhotoRepository } from "@/database/repositories/photo"
import { SessionRepository } from "@/database/repositories/session"
import { PredictResponse } from "@/services/model/modelTypes"
import { GPSWithAccuracy } from "@/utils/gps"
import { logger } from "@/utils/logging"
import { InstahomePhotoType } from "@/utils/types"

export const dexie = new Dexie("instahome") as DexieExtended

const originalDexieSchema = {
  [TableNames.session]: "++dexieId,&id",
  [TableNames.photo]: "++dexieId,&id,deleted,instahomeSessionId,wallNumber,uploadedAt,modelResult,type,amenityId",
  [TableNames.detail]: "++dexieId,&id,deleted,uploadedAt,instahomeSessionId,instahomePhotoId",
  [TableNames.orderedStep]: "++dexieId,&name,instahomeSessionId",
  [TableNames.amenity]: "++dexieId,&id,instahomeSessionId,type,skipped,deletedAt",
}

dexie.version(60).stores(originalDexieSchema)
dexie.version(61).stores({
  [TableNames.detail]: "++dexieId,&id,deleted,uploadedAt,instahomeSessionId,instahomePhotoId,modelResult",
})
dexie.version(62).stores({
  [TableNames.photo]:
    "++dexieId,&id,deleted,instahomeSessionId,wallNumber,uploadedAt,uploadFailed,modelResult,type,amenityId",
  [TableNames.detail]: "++dexieId,&id,deleted,uploadedAt,uploadFailed,instahomeSessionId,instahomePhotoId,modelResult",
})
dexie
  .version(63)
  .stores({
    [TableNames.photo]:
      "++dexieId,&id,deleted,instahomeSessionId,wallNumber,uploadedAt,uploadFailed,uploadProgress,modelResult,type,amenityId",
    [TableNames.detail]:
      "++dexieId,&id,deleted,uploadedAt,uploadFailed,uploadProgress,instahomeSessionId,instahomePhotoId,modelResult",
  })
  .upgrade(async (tx) => {
    // Backfill uploadProgress for records that pre-date the field:
    // already-uploaded → 101, everything else → -1 (awaiting upload).
    await tx
      .table("photo")
      .toCollection()
      .modify((p: { uploadProgress?: number; uploadedAt?: Date }) => {
        if (p.uploadProgress === undefined) p.uploadProgress = p.uploadedAt ? 101 : -1
      })
    await tx
      .table("detail")
      .toCollection()
      .modify((d: { uploadProgress?: number; uploadedAt?: Date }) => {
        if (d.uploadProgress === undefined) d.uploadProgress = d.uploadedAt ? 101 : -1
      })
  })

export const db = {
  session: new SessionRepository({ dexie, tableName: "session" }),
  photo: new PhotoRepository({ dexie, tableName: "photo" }),
  detail: new DetailRepository({ dexie, tableName: "detail" }),
  orderedStep: new OrderedStepRepository({ dexie, tableName: "orderedStep" }),
  amenity: new AmenityRepository({ dexie, tableName: "amenity" }),
} as const

type AddPhotoParams = {
  id?: string
  sessionId: string
  file: File
  gps?: GPSWithAccuracy
  type: InstahomePhotoType
  amenityId?: string

  // If photo of wall, otherwise undefined
  wallNumber?: number
  buildingPolygonGeoJson?: GeoJsonObj
  photoCoordinatesGeoJson?: GeoJsonObj
  visibleWallsGeoJson?: GeoJsonObj
  // after forcePointOutsidePolygon: true, BE returns adjustedGPS
  adjustedGps?: { latitude: number; longitude: number }

  modelResult: PredictResponse
}

/**
 * Adds a photo to the IndexedDB.
 * @param params
 */
export async function addPhotoToDB(params: AddPhotoParams) {
  const {
    sessionId,
    file,
    gps,
    wallNumber,
    id,
    type,
    amenityId,
    buildingPolygonGeoJson,
    photoCoordinatesGeoJson,
    visibleWallsGeoJson,
    adjustedGps,
    modelResult,
  } = params

  const buffer = await file.arrayBuffer()
  const uint8 = new Uint8Array(buffer)

  await db.photo.add({
    id: id ?? cuid2.createId(),
    instahomeSessionId: sessionId,
    wallNumber: wallNumber ?? undefined,
    latitude: gps?.latitude,
    longitude: gps?.longitude,
    accuracy: gps?.accuracy,
    adjustedLatitude: adjustedGps?.latitude,
    adjustedLongitude: adjustedGps?.longitude,
    buildingPolygonGeoJson,
    photoCoordinatesGeoJson,
    visibleWallsGeoJson,

    data: uint8,
    mime: file.type,

    createdAt: new Date(),
    updatedAt: new Date(),
    type,
    amenityId,

    modelResult,
    uploadProgress: -1,
  })
}

type RetakeMainPhotoparams = {
  oldPhotoId: string
  newPhotoId: string
  file: File
  sessionId: string
  gps?: GPSWithAccuracy
  wallNumber?: number
  buildingPolygonGeoJson?: GeoJsonObj
  photoCoordinatesGeoJson?: GeoJsonObj
  visibleWallsGeoJson?: GeoJsonObj
  adjustedGps?: { latitude: number; longitude: number }
  modelResult: PredictResponse
}

export async function retakeMainPhotoAndMigrateDetails(params: RetakeMainPhotoparams) {
  const {
    oldPhotoId,
    newPhotoId,
    file,
    sessionId,
    wallNumber,
    gps,
    buildingPolygonGeoJson,
    photoCoordinatesGeoJson,
    visibleWallsGeoJson,
    adjustedGps,
    modelResult,
  } = params

  const buffer = await file.arrayBuffer()
  const data = new Uint8Array(buffer)

  await dexie.transaction("rw", [dexie.photo, dexie.detail], async () => {
    // mark old photo as deleted - will be deleted on server by background uploader
    await db.photo.markAsDeleted(oldPhotoId)

    // Save new photo directly (not via addPhotoToDB, since binary data is already computed)
    const currentDate = new Date()
    await dexie.photo.add({
      id: newPhotoId,
      instahomeSessionId: sessionId,
      wallNumber,
      latitude: gps?.latitude,
      longitude: gps?.longitude,
      accuracy: gps?.accuracy,
      adjustedLatitude: adjustedGps?.latitude,
      adjustedLongitude: adjustedGps?.longitude,
      type: "Wall",
      buildingPolygonGeoJson,
      photoCoordinatesGeoJson,
      visibleWallsGeoJson,
      data,
      mime: file.type,
      createdAt: currentDate,
      updatedAt: currentDate,
      modelResult,
      uploadProgress: -1,
    })

    // update details to point to the new wall photo
    const details = await db.detail.findByMainPhoto({ instahomePhotoId: oldPhotoId, includeDeleted: true })
    await Promise.all(
      details.map(async (detail) => {
        // uploadedAt is kept on purpose: it tells the uploader whether the server already holds
        // this detail (and should move it to new photo) or detail is not uploaded yet and will be uploaded directly under new parent
        await db.detail.update(detail.id, {
          instahomePhotoId: newPhotoId,
          newInstahomePhotoId: newPhotoId,
        })
      }),
    )
  })
}

type AddDetailParams = {
  id: string
  sessionId: string
  instahomePhotoId: string
  file: File
  gps?: GPSWithAccuracy
  modelResult: PredictResponse
}

/**
 * Upserts a photo detail to the IndexedDB.
 * @param params
 */
export async function upsertDetailToDB(params: AddDetailParams) {
  const { sessionId, instahomePhotoId, file, gps, id, modelResult } = params

  const buffer = await file.arrayBuffer()
  const imageData = new Uint8Array(buffer)

  await db.detail.upsert({
    id,
    instahomeSessionId: sessionId,
    instahomePhotoId,
    latitude: gps?.latitude,
    longitude: gps?.longitude,
    accuracy: gps?.accuracy,

    data: imageData,
    mime: file.type,

    createdAt: new Date(),
    updatedAt: new Date(),
    uploadedAt: undefined,

    modelResult,
    uploadProgress: -1,
  })
}

type SetAmenitySkippedParams = {
  amenityId: string
  value: boolean
}

/**
 * Sets skipped state for amenity in IndexedDB
 * @param params Parameters including amenityId and skipped value
 */
export async function setAmenitySkipped(params: SetAmenitySkippedParams) {
  const { amenityId, value } = params

  const amenity = await db.amenity.get(amenityId)
  if (!amenity) {
    logger.error(`Amenity with id ${amenityId} not found to skip`)
    return
  }

  logger.info(`Skipping amenity with id ${amenityId} and type ${amenity.type}`)
  await db.amenity.update({ amenityId: amenityId, data: { skipped: value } })
}

type AmenityWithPhotos<TAmenityType extends InstahomeAmenityType> = Amenity<TAmenityType> & {
  photos: Photo[]
}

/**
 * Calculates next amenity to navigate to
 * @param sessionId Instahome session ID
 * @param amenityGroup Amenity group
 * @returns Next amenity with photos or undefined if all amenities are done
 */
export async function getNextAmenity<TAmenityTypes extends InstahomeAmenityType>(
  sessionId: string,
  amenityGroup: AmenityGroup,
) {
  const amenitiesByGroup = await db.amenity.getByAmenityGroup<TAmenityTypes>(sessionId, amenityGroup)
  const amenitiesWithPhotos: AmenityWithPhotos<TAmenityTypes>[] = await Promise.all(
    amenitiesByGroup.map(async (amenity) => {
      const photos = await db.photo.findByAmenityId({ sessionId, amenityId: amenity.id })
      return { ...amenity, photos }

      // TODO Dexie podporuje joiny, len tu nefunguje TS, treba zistit ako to rozbehat
      // https://dexie.org/docs/API-Reference#joining
      // const photos = await dexie.table("photo").where("amenityId").equals(amenity.id).toArray()
      // return { ...amenity, photos }
    }),
  )

  const nextAmenity = amenitiesWithPhotos.find((amenity) => amenity.photos.length === 0 && !amenity.skipped)
  return nextAmenity
}
